/**
 * Promo code capture + persistence for cross-page handoff.
 *
 * QR scans and shared URLs land with `?promo=MEETS26` (plus optional
 * UTM params). We persist that to localStorage so the Upgrade page
 * can apply it even if the user navigates between landing → signin →
 * upgrade. 24h TTL — campaign codes are short-lived, no need to keep
 * forever.
 */

const STORAGE_KEY = "rh_promo";
const TTL_MS = 24 * 60 * 60 * 1000;

export interface CapturedPromo {
  code: string;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  ts: number;
}

/** Read promo from URL (typed by user / scanned from QR). Uppercases. */
export function readPromoFromUrl(search: string = window.location.search): CapturedPromo | null {
  const params = new URLSearchParams(search);
  const code = params.get("promo")?.trim().toUpperCase();
  if (!code) return null;
  return {
    code,
    source: params.get("utm_source"),
    medium: params.get("utm_medium"),
    campaign: params.get("utm_campaign"),
    ts: Date.now(),
  };
}

/** Persist a captured promo for cross-page handoff. */
export function persistPromo(promo: CapturedPromo): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(promo));
  } catch {
    // localStorage may be unavailable (private mode, etc.); ignore.
  }
}

/** Read persisted promo if present and not expired. */
export function readPersistedPromo(): CapturedPromo | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CapturedPromo;
    if (!parsed.code) return null;
    if (Date.now() - parsed.ts > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Resolve current promo: URL takes precedence, else persisted. */
export function getActivePromo(): CapturedPromo | null {
  return readPromoFromUrl() ?? readPersistedPromo();
}

/** Clear the persisted promo (e.g., after successful redemption). */
export function clearPromo(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
